import { useState } from 'react';
import { format, subDays } from 'date-fns';
import { reportsApi, type ExportFormat } from '@/api/reports.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DateRangeFields } from '@/components/ui/DateRangeFields';
import { StatCard } from '@/components/ui/StatCard';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { useAppSelector } from '@/hooks/useAppSelector';
import { formatCurrency } from '@/utils/format';
import type {
  EmployeeSalesReportDto,
  ExpiryReportDto,
  InventoryReportDto,
  LowStockReportDto,
  LowStockReportLineDto,
  ProductSalesReportDto,
  ProfitReportDto,
  SalesReportDto,
  TaxReportDto,
} from '@/types/report';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { downloadCsv } from '@/utils/exportCsv';
import { showApiError } from '@/utils/apiError';

import { notify } from '@/core/toast/notify';
type ReportTab = 'sales' | 'products' | 'profit' | 'tax' | 'inventory' | 'lowstock' | 'expiry' | 'employees';

export function ReportsPage() {
  const selectedStore = useAppSelector((s) => s.app.selectedStore);
  const storeId = selectedStore?.id;
  const currency = selectedStore?.currencyCode ?? 'INR';
  const fmt = (n: number | string | null | undefined) => formatCurrency(n, currency);
  const [tab, setTab] = useState<ReportTab>('sales');
  const [from, setFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [to, setTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [sales, setSales] = useState<SalesReportDto | null>(null);
  const [inventory, setInventory] = useState<InventoryReportDto | null>(null);
  const [productSales, setProductSales] = useState<ProductSalesReportDto | null>(null);
  const [profit, setProfit] = useState<ProfitReportDto | null>(null);
  const [tax, setTax] = useState<TaxReportDto | null>(null);
  const [lowStock, setLowStock] = useState<LowStockReportDto | null>(null);
  const [expiry, setExpiry] = useState<ExpiryReportDto | null>(null);
  const [employeeSales, setEmployeeSales] = useState<EmployeeSalesReportDto | null>(null);
  const [loading, setLoading] = useState(false);

  const financialTabs: ReportTab[] = ['sales', 'products', 'profit', 'tax', 'employees'];

  const load = async () => {
    if (financialTabs.includes(tab) && !storeId) {
      notify.error('Select a store for financial reports');
      return;
    }
    setLoading(true);
    try {
      switch (tab) {
        case 'sales':
          setSales(await reportsApi.sales(from, to, storeId));
          break;
        case 'products':
          setProductSales(await reportsApi.productSales(from, to, storeId));
          break;
        case 'profit':
          setProfit(await reportsApi.profit(from, to, storeId));
          break;
        case 'tax':
          setTax(await reportsApi.tax(from, to, storeId));
          break;
        case 'inventory':
          setInventory(await reportsApi.inventory(storeId));
          break;
        case 'lowstock':
          if (!storeId) {
            notify.error('Select a store in the header for low stock report');
            return;
          }
          setLowStock(await reportsApi.lowStock(storeId));
          break;
        case 'expiry':
          setExpiry(await reportsApi.expiry(storeId));
          break;
        case 'employees':
          setEmployeeSales(await reportsApi.employeeSales(from, to, storeId));
          break;
      }
    } catch (err) {
      showApiError(err, 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (formatType: ExportFormat) => {
    try {
      if (tab === 'sales') {
        await reportsApi.exportSales(from, to, formatType, storeId);
        notify.success(`Exported as ${formatType.toUpperCase()}`);
        return;
      }
      if (tab === 'inventory') {
        await reportsApi.exportInventory(formatType, storeId);
        notify.success(`Exported as ${formatType.toUpperCase()}`);
        return;
      }
      if (formatType !== 'csv') {
        notify.error('PDF/Excel export is available for Sales and Inventory tabs only');
        return;
      }
      switch (tab) {
        case 'products':
          if (!productSales?.lines?.length) {
            notify.error('Load product sales report first');
            return;
          }
          downloadCsv(
            `product-sales-${from}-${to}.csv`,
            ['SKU', 'Product', 'Qty Sold', 'Revenue'],
            productSales.lines.map((l) => [l.sku, l.productName, l.quantitySold, l.salesAmount]),
          );
          break;
        case 'profit':
          if (!profit) {
            notify.error('Load profit report first');
            return;
          }
          downloadCsv(`profit-${from}-${to}.csv`, ['Metric', 'Value'], [
            ['Revenue', profit.totalRevenue],
            ['Cost', profit.totalCost],
            ['Profit', profit.totalProfit],
          ]);
          break;
        case 'tax':
          if (!tax) {
            notify.error('Load tax report first');
            return;
          }
          downloadCsv(
            `tax-${from}-${to}.csv`,
            ['Tax rate %', 'Taxable', 'Tax amount'],
            (tax.breakdown ?? []).map((r) => [r.taxPercentage, r.taxableAmount, r.taxAmount]),
          );
          break;
        case 'lowstock':
          if (!lowStock?.items?.length) {
            notify.error('Load low stock report first');
            return;
          }
          downloadCsv(
            'low-stock.csv',
            ['Product', 'SKU', 'On hand', 'Alert qty', 'Need'],
            lowStock.items.map((l) => [
              l.productName,
              l.sku,
              l.currentQuantity,
              l.alertQuantity,
              l.shortfall,
            ]),
          );
          break;
        case 'expiry':
          if (!expiry?.items?.length) {
            notify.error('Load expiry report first');
            return;
          }
          downloadCsv(
            'expiry.csv',
            ['Product', 'Batch', 'Expiry', 'Qty', 'Days left'],
            expiry.items.map((l) => [
              l.productName,
              l.batchNumber ?? '',
              l.expiryDate,
              l.quantity,
              l.daysUntilExpiry,
            ]),
          );
          break;
        case 'employees':
          if (!employeeSales?.lines?.length) {
            notify.error('Load employee sales report first');
            return;
          }
          downloadCsv(
            `employee-sales-${from}-${to}.csv`,
            ['Employee', 'Invoices', 'Sales'],
            employeeSales.lines.map((l) => [l.employeeName, l.invoiceCount, l.totalSales]),
          );
          break;
        default:
          return;
      }
      notify.success('Exported as CSV');
    } catch (err) {
      showApiError(err, 'Export failed');
    }
  };

  const tabs: { id: ReportTab; label: string }[] = [
    { id: 'sales', label: 'Sales' },
    { id: 'products', label: 'Product Sales' },
    { id: 'profit', label: 'Profit' },
    { id: 'tax', label: 'Tax' },
    { id: 'inventory', label: 'Inventory' },
    { id: 'lowstock', label: 'Low Stock' },
    { id: 'expiry', label: 'Expiry' },
    { id: 'employees', label: 'Employee Sales' },
  ];

  const productColumns: Column<ProductSalesReportDto['lines'][0]>[] = [
    { key: 'sku', header: 'SKU' },
    { key: 'productName', header: 'Product' },
    { key: 'quantitySold', header: 'Qty Sold' },
    { key: 'salesAmount', header: 'Revenue', render: (r) => fmt(r.salesAmount) },
  ];

  return (
    <div>
      <PageHeader
        title="Reports"
        description={
          selectedStore
            ? `${selectedStore.name} · ${currency} — financial reports require a store`
            : 'Select a store in the header for financial reports'
        }
        breadcrumbs={[{ label: 'POS', to: '/pos/dashboard' }, { label: 'Reports' }]}
      />
      <div className="mb-4 flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              tab === t.id ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-700'
            }`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="card mb-6 flex flex-wrap items-end gap-4 p-4">
        {!['inventory', 'lowstock', 'expiry'].includes(tab) && (
          <>
            <DateRangeFields from={from} to={to} onFromChange={setFrom} onToChange={setTo} className="!w-auto" />
          </>
        )}
        <Button onClick={load} loading={loading}>
          Generate
        </Button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => exportReport('csv')}>
            CSV
          </Button>
          <Button variant="secondary" onClick={() => exportReport('excel')}>
            Excel
          </Button>
          <Button variant="secondary" onClick={() => exportReport('pdf')}>
            PDF
          </Button>
        </div>
      </div>

      {sales && tab === 'sales' && (
        <>
          <div className="grid gap-4 sm:grid-cols-4">
            <StatCard title="Invoices" value={sales.totalInvoices} />
            <StatCard title="Total Sales" value={fmt(sales.totalSales)} />
            <StatCard title="Tax" value={fmt(sales.totalTax)} />
            <StatCard title="Discounts" value={fmt(sales.totalDiscount)} />
          </div>
          <div className="card mt-6 p-4">
            <h3 className="mb-4 font-semibold">Daily breakdown</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={sales.dailyBreakdown ?? []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="salesAmount" fill="#2563eb" name="Sales" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {productSales && tab === 'products' && (
        <DataTable columns={productColumns} data={productSales.lines} keyField="productId" loading={loading} />
      )}

      {profit && tab === 'profit' && (
        <>
          <div className="mb-4 grid gap-4 sm:grid-cols-4">
            <StatCard title="Revenue" value={fmt(profit.totalRevenue)} />
            <StatCard title="Cost" value={fmt(profit.totalCost)} />
            <StatCard title="Gross Profit" value={fmt(profit.totalProfit)} />
            <StatCard
              title="Margin %"
              value={
                profit.totalRevenue
                  ? `${((profit.totalProfit / profit.totalRevenue) * 100).toFixed(1)}%`
                  : '0%'
              }
            />
          </div>
          <p className="mb-3 text-xs text-slate-500">
            Profit per product = sales revenue (from invoices) minus cost (purchase unit price × qty sold).
            Record purchases in Inventory so cost price updates; selling price comes from POS / store product.
          </p>
          <DataTable columns={[
              { key: 'productName', header: 'Product' },
              { key: 'quantitySold', header: 'Qty sold' },
              {
                key: 'unitSellingRevenue',
                header: 'Avg sell',
                render: (r) => fmt(r.unitSellingRevenue ?? 0),
              },
              { key: 'unitCost', header: 'Unit cost', render: (r) => fmt(r.unitCost ?? 0) },
              { key: 'revenue', header: 'Revenue', render: (r) => fmt(r.revenue) },
              { key: 'cost', header: 'Cost', render: (r) => fmt(r.cost) },
              { key: 'profit', header: 'Profit', render: (r) => fmt(r.profit) },
              {
                key: 'marginPercent',
                header: 'Margin %',
                render: (r) => `${Number(r.marginPercent ?? 0).toFixed(1)}%`,
              },
            ]}
            data={profit.lines ?? []}
            keyField="productId"
            loading={loading}
          />
        </>
      )}

      {tax && tab === 'tax' && (
        <>
          <div className="mb-4 grid gap-4 sm:grid-cols-2">
            <StatCard title="Taxable Amount" value={fmt(tax.totalTaxable)} />
            <StatCard title="Total Tax" value={fmt(tax.totalTax)} />
          </div>
          <DataTable columns={[
              { key: 'taxPercentage', header: 'Rate %' },
              { key: 'taxableAmount', header: 'Taxable', render: (r) => fmt(r.taxableAmount) },
              { key: 'taxAmount', header: 'Tax', render: (r) => fmt(r.taxAmount) },
              { key: 'lineCount', header: 'Lines' },
            ]}
            data={(tax.breakdown ?? []).map((row, i) => ({ ...row, id: i }))}
            keyField="id"
          />
        </>
      )}

      {inventory && tab === 'inventory' && (
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard title="Products" value={inventory.totalProducts} />
          <StatCard title="Stock Value" value={fmt(inventory.totalStockValue)} />
          <StatCard title="Low Stock" value={inventory.lowStockCount} />
        </div>
      )}

      {lowStock && tab === 'lowstock' && (
        <>
          <div className="mb-4 grid gap-4 sm:grid-cols-2">
            <StatCard title="Low stock items" value={lowStock.totalLowStockItems} />
            <StatCard title="Store" value={selectedStore?.name ?? '—'} />
          </div>
          <p className="mb-3 text-xs text-slate-500">
            Uses thresholds from{' '}
            <a href="/pos/product-alerts" className="font-medium text-primary-600 underline">
              Product alerts
            </a>{' '}
            for the selected store. Cost and stock value on the Inventory tab come from purchases /
            store product cost.
          </p>
          <DataTable<LowStockReportLineDto>
            columns={[
              { key: 'sku', header: 'SKU' },
              { key: 'productName', header: 'Product' },
              {
                key: 'currentQuantity',
                header: 'On hand',
                render: (r: LowStockReportLineDto) => r.currentQuantity,
              },
              { key: 'alertQuantity', header: 'Alert qty' },
              { key: 'shortfall', header: 'Need', render: (r: LowStockReportLineDto) => r.shortfall },
            ]}
            data={lowStock.items}
            keyField={(r: LowStockReportLineDto) => `${r.productId}-${r.storeId ?? 0}`}
            loading={loading}
          />
        </>
      )}

      {expiry && tab === 'expiry' && (
        <DataTable columns={[
            { key: 'sku', header: 'SKU' },
            { key: 'productName', header: 'Product' },
            { key: 'expiryDate', header: 'Expiry' },
            { key: 'daysUntilExpiry', header: 'Days Left' },
            { key: 'quantity', header: 'Qty' },
          ]}
          data={expiry.items}
          keyField="productId"
        />
      )}

      {employeeSales && tab === 'employees' && (
        <DataTable columns={[
            { key: 'employeeName', header: 'Employee' },
            { key: 'invoiceCount', header: 'Invoices' },
            { key: 'totalSales', header: 'Sales', render: (r) => fmt(r.totalSales) },
          ]}
          data={employeeSales.lines}
          keyField="employeeId"
        />
      )}
    </div>
  );
}
